import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Typography,
    Badge,
    TextField,
    InputAdornment,
    IconButton,
    Fab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Paper,
    Divider,
    CircularProgress
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Send as SendIcon,
    Group as GroupIcon,
    Person as PersonIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import TEXT from './text';
import io from 'socket.io-client';
import * as chatActions from '../../../Redux/chatReducer/Actions';

let socket;

const Chat = () => {
    const dispatch = useDispatch();
    const language = useSelector(state => state.App_Settings.language);
    const userMetadata = useSelector(state => state.UserMetaData);
    const { rooms, activeRoom, messages, typingUsers } = useSelector(state => state.Chat);

    const [searchQuery, setSearchQuery] = useState('');
    const [messageInput, setMessageInput] = useState('');
    const [newChatDialog, setNewChatDialog] = useState(false);
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [creatingGroupChat, setCreatingGroupChat] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [typingTimeout, setTypingTimeout] = useState(null);
    const [isTyping, setIsTyping] = useState(false);
    const [showChatView, setShowChatView] = useState(false);
    const messagesEndRef = useRef(null);

    // Initialize Socket.IO
    useEffect(() => {
        socket = io(window.devMode ? 'http://localhost:80' : window.location.origin, {
            transports: ['polling'], // Use polling only, avoid WebSocket upgrade issues
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: Infinity
        });

        socket.on('connect', () => {
            console.log('[Chat] Connected to socket server');
        });

        socket.on('new_message', (message) => {
            console.log('[Chat] New message received:', message);
            dispatch(chatActions.addMessage(message));
        });

        socket.on('user_typing', ({ room_id, user_id, user_name, is_typing }) => {
            if (user_id !== userMetadata.uid) {
                dispatch(chatActions.setTyping(room_id, user_id, user_name, is_typing));
            }
        });

        return () => {
            if (socket) socket.disconnect();
        };
    }, [dispatch, userMetadata.uid]);

    // Load chat rooms
    useEffect(() => {
        loadChatRooms();
    }, []);

    // Join active room
    useEffect(() => {
        if (activeRoom && socket) {
            socket.emit('join_chat_room', {
                room_id: activeRoom,
                user_id: userMetadata.uid
            });

            loadMessages(activeRoom);
            dispatch(chatActions.clearUnread(activeRoom));
        }

        return () => {
            if (activeRoom && socket) {
                socket.emit('leave_chat_room', {
                    room_id: activeRoom,
                    user_id: userMetadata.uid
                });
            }
        };
    }, [activeRoom]);

    // Scroll to bottom of messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, activeRoom]);

    const loadChatRooms = async () => {
        try {
            const response = await fetch(
                window.devMode ? 'http://localhost:80/chat/get_rooms' : '/chat/get_rooms',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: userMetadata.email })
                }
            );

            const data = await response.json();
            if (data.status === 'success') {
                dispatch(chatActions.setRooms(data.rooms));
            }
        } catch (error) {
            console.error('[Chat] Error loading rooms:', error);
        }
    };

    const loadMessages = async (roomId) => {
        try {
            const response = await fetch(
                window.devMode ? 'http://localhost:80/chat/get_messages' : '/chat/get_messages',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: userMetadata.email,
                        room_id: roomId
                    })
                }
            );

            const data = await response.json();
            if (data.status === 'success') {
                dispatch(chatActions.setMessages(roomId, data.messages));
            }
        } catch (error) {
            console.error('[Chat] Error loading messages:', error);
        }
    };

    const searchUsers = async (query) => {
        if (!query.trim()) {
            setUserSearchResults([]);
            return;
        }

        try {
            const response = await fetch(
                window.devMode ? 'http://localhost:80/chat/search_users' : '/chat/search_users',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: userMetadata.email,
                        query
                    })
                }
            );

            const data = await response.json();
            if (data.status === 'success') {
                setUserSearchResults(data.users);
            }
        } catch (error) {
            console.error('[Chat] Error searching users:', error);
        }
    };

    const createDirectChat = async (userId) => {
        try {
            const response = await fetch(
                window.devMode ? 'http://localhost:80/chat/create_room' : '/chat/create_room',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: userMetadata.email,
                        room_type: 'direct',
                        participant_ids: [userId]
                    })
                }
            );

            const data = await response.json();
            if (data.status === 'success') {
                await loadChatRooms();
                dispatch(chatActions.setActiveRoom(data.room_id));
                setNewChatDialog(false);
                setUserSearchQuery('');
                setUserSearchResults([]);
            }
        } catch (error) {
            console.error('[Chat] Error creating direct chat:', error);
        }
    };

    const createGroupChat = async () => {
        if (!groupName.trim() || selectedUsers.length === 0) return;

        try {
            const response = await fetch(
                window.devMode ? 'http://localhost:80/chat/create_room' : '/chat/create_room',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: userMetadata.email,
                        room_type: 'group',
                        room_name: groupName,
                        participant_ids: selectedUsers
                    })
                }
            );

            const data = await response.json();
            if (data.status === 'success') {
                await loadChatRooms();
                dispatch(chatActions.setActiveRoom(data.room_id));
                setNewChatDialog(false);
                setCreatingGroupChat(false);
                setGroupName('');
                setSelectedUsers([]);
                setUserSearchQuery('');
                setUserSearchResults([]);
            }
        } catch (error) {
            console.error('[Chat] Error creating group chat:', error);
        }
    };

    const sendMessage = () => {
        if (!messageInput.trim() || !activeRoom || !socket) return;

        socket.emit('send_message', {
            room_id: activeRoom,
            sender_id: userMetadata.uid,
            message_content: messageInput.trim()
        });

        setMessageInput('');
        setIsTyping(false);
    };

    const handleTyping = (e) => {
        setMessageInput(e.target.value);

        if (!socket || !activeRoom) return;

        if (!isTyping) {
            setIsTyping(true);
            socket.emit('typing', {
                room_id: activeRoom,
                user_id: userMetadata.uid,
                user_name: userMetadata.fullName,
                is_typing: true
            });
        }

        if (typingTimeout) clearTimeout(typingTimeout);

        const timeout = setTimeout(() => {
            setIsTyping(false);
            socket.emit('typing', {
                room_id: activeRoom,
                user_id: userMetadata.uid,
                user_name: userMetadata.fullName,
                is_typing: false
            });
        }, 2000);

        setTypingTimeout(timeout);
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp * 1000);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else if (date.toDateString() === yesterday.toDateString()) {
            return TEXT.Yesterday[language];
        } else {
            return date.toLocaleDateString();
        }
    };

    const filteredRooms = rooms.filter(room =>
        room.room_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleRoomSelect = (roomId) => {
        dispatch(chatActions.setActiveRoom(roomId));
        setShowChatView(true);
    };

    const handleBackToList = () => {
        setShowChatView(false);
    };

    const activeRoomData = rooms.find(r => r.room_id === activeRoom);
    const activeRoomMessages = messages[activeRoom] || [];
    const activeRoomTyping = typingUsers[activeRoom] || {};
    const typingUserNames = Object.values(activeRoomTyping);

    return (
        <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Grid container sx={{ height: '100%', flexWrap: 'nowrap' }}>
                {/* Chat List */}
                <Grid 
                    item 
                    xs={12} 
                    // md={4} 
                    sx={{ 
                        borderRight: { md: 1 }, 
                        borderColor: 'divider', 
                        height: '100%', 
                        display: { xs: showChatView ? 'none' : 'flex', md: 'flex' },
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            fullWidth
                            placeholder={TEXT.SearchUsers[language]}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                            size="small"
                        />
                        <IconButton 
                            color="primary" 
                            onClick={() => setNewChatDialog(true)}
                            sx={{ 
                                bgcolor: 'primary.main', 
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.dark' }
                            }}
                        >
                            <AddIcon />
                        </IconButton>
                    </Box>
                    
                    <List sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
                        {filteredRooms.map(room => (
                            <ListItem key={room.room_id} disablePadding>
                                <ListItemButton
                                    selected={activeRoom === room.room_id}
                                    onClick={() => handleRoomSelect(room.room_id)}
                                >
                                    <ListItemAvatar>
                                        <Badge badgeContent={room.unread_count} color="error">
                                            <Avatar
                                                src={room.room_type === 'direct' && room.participants[0]?.profile_image}
                                            >
                                                {room.room_type === 'group' ? <GroupIcon /> : <PersonIcon />}
                                            </Avatar>
                                        </Badge>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={room.room_name}
                                        secondary={room.last_message || TEXT.NoMessages[language]}
                                        secondaryTypographyProps={{
                                            noWrap: true,
                                            // sx: { maxWidth: '200px' }
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {room.last_message_time && formatTimestamp(room.last_message_time)}
                                    </Typography>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Grid>

                {/* Chat Messages */}
                <Grid 
                    item 
                    xs={12} 
                    md={8} 
                    sx={{ 
                        height: '100%', 
                        display: { xs: showChatView ? 'flex' : 'none', md: 'flex' },
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    {activeRoom ? (
                        <>
                            {/* Chat Header */}
                            <Paper sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }} elevation={1}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <IconButton 
                                        onClick={handleBackToList}
                                        sx={{ display: { xs: 'flex', md: 'none' } }}
                                    >
                                        <ArrowBackIcon />
                                    </IconButton>
                                    <Avatar src={activeRoomData?.room_type === 'direct' && activeRoomData?.participants[0]?.profile_image}>
                                        {activeRoomData?.room_type === 'group' ? <GroupIcon /> : <PersonIcon />}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6">{activeRoomData?.room_name}</Typography>
                                        {typingUserNames.length > 0 && (
                                            <Typography variant="caption" color="primary">
                                                {typingUserNames.join(', ')} {TEXT.Typing[language]}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Paper>

                            {/* Messages Area */}
                            <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2, minHeight: 0 }}>
                                {activeRoomMessages.length === 0 ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Typography color="text.secondary">
                                            {TEXT.NoMessages[language]}
                                        </Typography>
                                    </Box>
                                ) : (
                                    activeRoomMessages.map((message, index) => {
                                        const isOwn = message.sender_id === userMetadata.uid;
                                        return (
                                            <Box
                                                key={message.message_id}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: isOwn ? 'flex-end' : 'flex-start',
                                                    mb: 2
                                                }}
                                            >
                                                {!isOwn && (
                                                    <Avatar
                                                        src={message.sender_image}
                                                        sx={{ width: 32, height: 32, mr: 1 }}
                                                    />
                                                )}
                                                <Paper
                                                    sx={{
                                                        p: 1.5,
                                                        // maxWidth: '70%',
                                                        backgroundColor: isOwn ? 'primary.main' : 'grey.100',
                                                        color: isOwn ? 'white' : 'text.primary'
                                                    }}
                                                    elevation={1}
                                                >
                                                    {!isOwn && (
                                                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                                            {message.sender_name}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="body2">{message.message_content}</Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: 'block',
                                                            textAlign: 'right',
                                                            mt: 0.5,
                                                            opacity: 0.7
                                                        }}
                                                    >
                                                        {formatTimestamp(message.timestamp)}
                                                    </Typography>
                                                </Paper>
                                            </Box>
                                        );
                                    })
                                )}
                                <div ref={messagesEndRef} />
                            </Box>

                            {/* Message Input */}
                            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                                <TextField
                                    fullWidth
                                    placeholder={TEXT.TypeMessage[language]}
                                    value={messageInput}
                                    onChange={handleTyping}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            sendMessage();
                                        }
                                    }}
                                    multiline
                                    maxRows={4}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={sendMessage} color="primary" disabled={!messageInput.trim()}>
                                                    <SendIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Typography variant="h6" color="text.secondary">
                                {TEXT.SelectChat[language]}
                            </Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>

            {/* New Chat Dialog */}
<Dialog open={newChatDialog} onClose={() => setNewChatDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {creatingGroupChat ? TEXT.CreateGroup[language] : TEXT.NewChat[language]}
                </DialogTitle>
                <DialogContent>
                    {creatingGroupChat && (
                        <TextField
                            fullWidth
                            label={TEXT.GroupName[language]}
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                    )}
                    <TextField
                        fullWidth
                        placeholder={TEXT.SearchUsers[language]}
                        value={userSearchQuery}
                        onChange={(e) => {
                            setUserSearchQuery(e.target.value);
                            searchUsers(e.target.value);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                    />
                    {creatingGroupChat && selectedUsers.length > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {selectedUsers.map(userId => {
                                const user = userSearchResults.find(u => u.user_id === userId);
                                return user && (
                                    <Chip
                                        key={userId}
                                        label={user.name}
                                        onDelete={() => setSelectedUsers(selectedUsers.filter(id => id !== userId))}
                                        avatar={<Avatar src={user.profile_image} />}
                                    />
                                );
                            })}
                        </Box>
                    )}
                    <List sx={{ mt: 2 }}>
                        {userSearchResults.map(user => (
                            <ListItem
                                key={user.user_id}
                                secondaryAction={
                                    creatingGroupChat ? (
                                        <IconButton
                                            onClick={() => {
                                                if (selectedUsers.includes(user.user_id)) {
                                                    setSelectedUsers(selectedUsers.filter(id => id !== user.user_id));
                                                } else {
                                                    setSelectedUsers([...selectedUsers, user.user_id]);
                                                }
                                            }}
                                        >
                                            {selectedUsers.includes(user.user_id) ? '✓' : '+'}
                                        </IconButton>
                                    ) : (
                                        <IconButton onClick={() => createDirectChat(user.user_id)}>
                                            <AddIcon />
                                        </IconButton>
                                    )
                                }
                            >
                                <ListItemAvatar>
                                    <Avatar src={user.profile_image}>
                                        <PersonIcon />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText primary={user.name} secondary={user.email} />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setNewChatDialog(false);
                        setCreatingGroupChat(false);
                        setGroupName('');
                        setSelectedUsers([]);
                        setUserSearchQuery('');
                        setUserSearchResults([]);
                    }}>
                        {TEXT.Cancel[language]}
                    </Button>
                    {!creatingGroupChat && (
                        <Button onClick={() => setCreatingGroupChat(true)} startIcon={<GroupIcon />}>
                            {TEXT.GroupChat[language]}
                        </Button>
                    )}
                    {creatingGroupChat && (
                        <Button
                            onClick={createGroupChat}
                            variant="contained"
                            disabled={!groupName.trim() || selectedUsers.length === 0}
                        >
                            {TEXT.Create[language]}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            
        </Box>
    );
};

export default Chat;
